import { parseContractComplianceError } from 'api/requests/contract-compliance-requests';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CONTRACT_COMPLIANCE__LOAD_ERROR } from 'translations/constants';
import { getContractComplianceErrorMessage } from '../utils';

export const useRuleDefaults = <TRule extends string, TValue extends { rule: TRule }>(
  fetchAll: () => Promise<TValue[]>,
) => {
  const [t] = useTranslation();
  const [defaults, setDefaults] = useState<Partial<Record<TRule, TValue>>>({});
  const [hasLoaded, setHasLoaded] = useState(false);
  const [loadErrorMessage, setLoadErrorMessage] = useState<string | null>(null);
  const latestRequest = useRef(0);

  const reload = useCallback(async () => {
    latestRequest.current += 1;
    const requestId = latestRequest.current;
    try {
      const values = await fetchAll();
      if (requestId !== latestRequest.current) return;
      setDefaults(values.reduce<Partial<Record<TRule, TValue>>>((map, value) => ({ ...map, [value.rule]: value }), {}));
      setLoadErrorMessage(null);
      setHasLoaded(true);
    } catch (error) {
      if (requestId !== latestRequest.current) return;
      setLoadErrorMessage(
        getContractComplianceErrorMessage(t, parseContractComplianceError(error), CONTRACT_COMPLIANCE__LOAD_ERROR),
      );
    }
  }, [fetchAll, t]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { defaults, hasLoaded, loadErrorMessage, reload };
};
